import { useEffect, useState } from 'react'
import { motion, useMotionValue, useSpring, useReducedMotion } from 'framer-motion'

/**
 * Floating background — drifting prism-color blobs, cursor-follow accent,
 * grain overlay. Designed for use behind hero / large sections.
 *
 * Pointer-events disabled so content above remains interactive.
 * Touch devices and prefers-reduced-motion users get a static / gently
 * auto-drifting fallback (no cursor tracking, no heavy animation).
 *
 * Usage: place inside a relatively-positioned container, full-bleed.
 *   <div className="relative">
 *     <FloatingBackground />
 *     <div className="relative">...content...</div>
 *   </div>
 */
export default function FloatingBackground({ className = '' }) {
  const reduceMotion = useReducedMotion()
  const [isTouch, setIsTouch] = useState(false)

  // Cursor-follow blob (lagged via spring)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const springX = useSpring(mouseX, { stiffness: 50, damping: 20 })
  const springY = useSpring(mouseY, { stiffness: 50, damping: 20 })

  useEffect(() => {
    const touch = matchMedia('(pointer: coarse)').matches
    setIsTouch(touch)
    if (touch || reduceMotion) return

    const handleMove = (e) => {
      mouseX.set(e.clientX - 250)
      mouseY.set(e.clientY - 250)
    }
    window.addEventListener('mousemove', handleMove)
    return () => window.removeEventListener('mousemove', handleMove)
  }, [mouseX, mouseY, reduceMotion])

  // Drifting blobs in the Prismscope logo palette
  // (deep navy #00008B, bright blue #3b82f6, violet #8b5cf6).
  // Opacity intentionally low — they should suggest depth, not dominate.
  const blobs = [
    { color: '#3b82f6', position: 'top-[-12%] left-[-8%]', size: 460, delay: '0s' },
    { color: '#8b5cf6', position: 'top-[25%] right-[-12%]', size: 500, delay: '-9s' },
    { color: '#00008B', position: 'bottom-[-18%] left-[25%]', size: 420, delay: '-18s' },
  ]

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {/* Drifting logo-color blobs (subtle) */}
      {blobs.map((b, i) => (
        <div
          key={i}
          className={`absolute rounded-full opacity-[0.13] mix-blend-multiply blur-3xl ${b.position} ${
            reduceMotion ? '' : 'animate-float-blob'
          }`}
          style={{
            width: b.size,
            height: b.size,
            backgroundColor: b.color,
            animationDelay: b.delay,
          }}
        />
      ))}

      {/* Cursor-follow accent (desktop only, very subtle) */}
      {!isTouch && !reduceMotion && (
        <motion.div
          className="absolute rounded-full opacity-[0.10] mix-blend-multiply blur-3xl"
          style={{
            width: 460,
            height: 460,
            backgroundColor: '#3b82f6',
            x: springX,
            y: springY,
          }}
        />
      )}

      {/* Grain overlay (retro texture) */}
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <filter id="grain-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#grain-noise)" />
      </svg>
    </div>
  )
}
